import type { IContextualMenuItem } from '@fluentui/react/lib/ContextualMenu';
import { FocusZoneDirection } from '@fluentui/react/lib/FocusZone';
import { getBoardViewSubMenuItems } from 'owa-calendar-board-commanding';
import { canUseCalendarBoard } from 'owa-calendar-board-launch';
import { changeCalendarViewType } from 'owa-calendar-module-commanding';
import { hasSelectedSaveView } from 'owa-calendar-module-commanding-utils';
import { getSelectedViewType } from 'owa-calendar-module-store';
import { getNumDaysInDayRange } from 'owa-calendar-surface-options-utils';
import { CalendarViewTypeEnum } from 'owa-calendar-view-type';
import { FilterableMenuItem, FilterableMenuItemArgs } from 'owa-filterable-menu';
import loc, { format } from 'owa-localize';
import {
    getBoardViewTypeDefinition,
    getDayViewTypeDefinition,
    getMonthViewTypeDefinition,
    getWeekViewTypeDefinition,
    getWorkWeekViewTypeDefinition,
} from './viewTypeDefinitions';
import {
    oneDaySubMenuOptionText,
    multiDaysSubMenuOptionText,
} from 'owa-calendar-shared-strings/lib/strings/calendarSurfaceCommandBar.locstring.json';

const SINGLE_DAY_VIEW_ONE_DAY = 1;
const SINGLE_DAY_VIEW_MAX_DAYS = 7;

const FLEXIBLE_DAY_MENU_WIDTH = 100;
const BOARD_VIEW_MENU_WIDTH = 212;
const BOARD_VIEW_MENU_MAX_HEIGHT = 540;

import styles from '../../components/CalendarModuleCommandBar.scss';

// TODO: VSO #88707 Separate module vs surface concerns in Calendar module
// Detailed view-switching concerns (e.g. resetting number of days in day range) need to be consolidated and handled elsewhere

export function getCalendarViewTypeMenuItems() {
    const isSavedViewSelected: boolean = hasSelectedSaveView();

    const getMenuItemClass = (
        viewType: CalendarViewTypeEnum,
        isSavedViewSelected: boolean,
        numOfDays?: number
    ) => {
        let isSelectedMenuItem = getSelectedViewType() === viewType && !isSavedViewSelected;
        if (typeof numOfDays === 'number') {
            isSelectedMenuItem = isSelectedMenuItem && getNumDaysInDayRange() === numOfDays;
        }
        return isSelectedMenuItem ? styles.viewOptionSelected : null;
    };

    const switchToSingleDayView = (numberOfDays: number): void => {
        const viewTypeChanged = getSelectedViewType() !== CalendarViewTypeEnum.Daily;
        const numDaysChanged = getNumDaysInDayRange() !== numberOfDays;
        if (viewTypeChanged || numDaysChanged) {
            changeCalendarViewType(CalendarViewTypeEnum.Daily, numberOfDays, isSavedViewSelected);
        }
    };

    const switchToWeek = () => {
        changeCalendarViewType(
            CalendarViewTypeEnum.Weekly,
            undefined /*newNumOfDaysInDayRange*/,
            isSavedViewSelected
        );
    };

    const switchToMonth = () => {
        changeCalendarViewType(
            CalendarViewTypeEnum.Monthly,
            undefined /*newNumOfDaysInDayRange*/,
            isSavedViewSelected
        );
    };

    const switchToWorkWeek = () => {
        changeCalendarViewType(
            CalendarViewTypeEnum.WorkWeek,
            undefined /*newNumOfDaysInDayRange*/,
            isSavedViewSelected
        );
    };

    const switchToBoard = () => {
        changeCalendarViewType(
            CalendarViewTypeEnum.Board,
            undefined /*newNumOfDaysInDayRange*/,
            isSavedViewSelected
        );
    };

    const createDayViewSubMenuItems = (numOfDays: number): IContextualMenuItem[] => {
        let singleDayViewSubMenuOptions: IContextualMenuItem[] = [];
        for (let dayIdx = 1; dayIdx <= numOfDays; dayIdx++) {
            const subMenuLabel =
                dayIdx == 1
                    ? format(loc(oneDaySubMenuOptionText), dayIdx)
                    : format(loc(multiDaysSubMenuOptionText), dayIdx);
            singleDayViewSubMenuOptions.push({
                text: subMenuLabel,
                key: subMenuLabel,
                title: subMenuLabel,
                onRenderIcon: () => null,
                className: getMenuItemClass(
                    CalendarViewTypeEnum.Daily,
                    isSavedViewSelected,
                    dayIdx
                ),
                onClick: () => switchToSingleDayView(dayIdx),
            });
        }
        return singleDayViewSubMenuOptions;
    };

    return [
        new FilterableMenuItem({
            ...getDayViewTypeDefinition(),
            [FilterableMenuItemArgs.onClick]: () => switchToSingleDayView(SINGLE_DAY_VIEW_ONE_DAY),
            [FilterableMenuItemArgs.className]: getMenuItemClass(
                CalendarViewTypeEnum.Daily,
                isSavedViewSelected
            ),
            [FilterableMenuItemArgs.split]: true,
            [FilterableMenuItemArgs.subMenuProps]: getFlexibleDayViewSubMenuProps(
                createDayViewSubMenuItems(SINGLE_DAY_VIEW_MAX_DAYS)
            ),
        }),
        new FilterableMenuItem({
            ...getWorkWeekViewTypeDefinition(),
            [FilterableMenuItemArgs.onClick]: switchToWorkWeek,
            [FilterableMenuItemArgs.className]: getMenuItemClass(
                CalendarViewTypeEnum.WorkWeek,
                isSavedViewSelected
            ),
        }),
        new FilterableMenuItem({
            ...getWeekViewTypeDefinition(),
            [FilterableMenuItemArgs.onClick]: switchToWeek,
            [FilterableMenuItemArgs.className]: getMenuItemClass(
                CalendarViewTypeEnum.Weekly,
                isSavedViewSelected
            ),
        }),
        new FilterableMenuItem({
            ...getMonthViewTypeDefinition(),
            [FilterableMenuItemArgs.onClick]: switchToMonth,
            [FilterableMenuItemArgs.className]: getMenuItemClass(
                CalendarViewTypeEnum.Monthly,
                isSavedViewSelected
            ),
        }),
        ...(canUseCalendarBoard()
            ? [
                  new FilterableMenuItem({
                      ...getBoardViewTypeDefinition(),
                      [FilterableMenuItemArgs.onClick]: switchToBoard,
                      [FilterableMenuItemArgs.className]: getMenuItemClass(
                          CalendarViewTypeEnum.Board,
                          isSavedViewSelected
                      ),
                      [FilterableMenuItemArgs.split]: true,
                      [FilterableMenuItemArgs.subMenuProps]: getBoardViewSubMenuProps(
                          switchToBoard
                      ),
                  }),
              ]
            : []),
    ];
}

function getFlexibleDayViewSubMenuProps(menuItems: IContextualMenuItem[]) {
    return {
        items: menuItems ? menuItems : null,
        gapSpace: 0,
        styles: {
            container: { width: FLEXIBLE_DAY_MENU_WIDTH },
            list: { width: FLEXIBLE_DAY_MENU_WIDTH },
        },
    };
}

function getBoardViewSubMenuProps(onSubMenuItemClick: () => void) {
    const menuItems = getBoardViewSubMenuItems(onSubMenuItemClick);
    return {
        items: menuItems ? menuItems : null,
        gapSpace: 0,
        focusZoneProps: {
            direction: FocusZoneDirection.bidirectional,
        },
        styles: {
            container: { width: BOARD_VIEW_MENU_WIDTH, maxHeight: BOARD_VIEW_MENU_MAX_HEIGHT },
            list: { width: BOARD_VIEW_MENU_WIDTH },
        },
    };
}
